/**
 * PassHeightSparkline — 🆕 2026-08 · Flugkurven-Sparkline (Höhenprofil).
 * Zeichnet z(t) = z0 + vz·t − ½·g_eff·t² aus dem 3D-Fit als Mini-SVG:
 * Wurfhöhe (Abwurf → Apex → Catch) und Distanz auf einen Blick.
 * Ehrlich bei fehlendem/veraltetem Fit (Platzhalter statt Fantasiekurve).
 */
import React from "react";

const CONF_COLOR = { high: "#34d399", medium: "#67e8f9", low: "#fbbf24" };

function Placeholder({ testId, label, title }) {
  return (
    <div
      data-testid={testId}
      title={title}
      className="h-14 w-24 shrink-0 border border-dashed border-border rounded-sm flex items-center justify-center font-mono text-[8px] uppercase tracking-widest text-muted-foreground text-center leading-tight"
    >
      {label}
    </div>
  );
}

export function PassHeightSparkline({ fit, lengthM, testId }) {
  if (!fit || !Array.isArray(fit.release_xyz_m)) {
    return (
      <Placeholder
        testId={`${testId}-missing`}
        label="kein 3D-Fit"
        title={`Kein Höhenprofil — „Flugbahn fitten“ im Vektor-Editor ausführen`}
      />
    );
  }
  const z0 = Number(fit.release_xyz_m[2]) || 0;
  const T = Number(fit.flight_time_s) || 0;
  const vz = Number((fit.release_velocity_mps || [0, 0, 0])[2]) || 0;
  const zc = Array.isArray(fit.catch_xyz_m) ? Number(fit.catch_xyz_m[2]) || 0 : 0;
  const apex = Number(fit.apex_z_m ?? z0) || 0;
  let g = Number(fit.g_eff_mps2) || 0;
  if (!g && T > 0) g = (2 * (z0 + vz * T - zc)) / (T * T);
  if (!(T > 0) || z0 < 0 || z0 > 10 || apex > 30 || apex < 0) {
    return (
      <Placeholder
        testId={`${testId}-stale`}
        label="Fit veraltet"
        title="3D-Fit stammt aus einem veralteten Koordinatenrahmen — Flugbahn im Vektor-Editor neu fitten"
      />
    );
  }
  const W = 96, H = 56, PX = 4, PY = 7;
  const yMax = Math.max(apex, 3) * 1.1;
  const N = 24;
  const pts = [];
  for (let i = 0; i <= N; i++) {
    const t = (T * i) / N;
    const z = Math.max(0, z0 + vz * t - 0.5 * g * t * t);
    pts.push([
      PX + ((W - 2 * PX) * i) / N,
      H - PY - ((H - 2 * PY) * z) / yMax,
      z,
    ]);
  }
  const path = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const groundY = H - PY;
  const area = `${path} L${pts[N][0].toFixed(1)},${groundY} L${pts[0][0].toFixed(1)},${groundY} Z`;
  const apexPt = pts.reduce((m, p) => (p[2] > m[2] ? p : m), pts[0]);
  const color = CONF_COLOR[fit.confidence] || CONF_COLOR.medium;
  const title = `Höhenprofil (3D-Fit, ${fit.confidence || "?"}): Abwurf ${z0.toFixed(1)} m → Apex ${apex.toFixed(1)} m → Catch ${zc.toFixed(1)} m · Flugzeit ${T.toFixed(1)} s · Distanz ${Number(lengthM ?? 0).toFixed(1)} m`;
  return (
    <svg
      data-testid={testId}
      viewBox={`0 0 ${W} ${H}`}
      className="h-14 w-24 shrink-0 border border-border rounded-sm bg-secondary"
      role="img"
    >
      <title>{title}</title>
      <line x1={PX} y1={groundY} x2={W - PX} y2={groundY} stroke="#52525b" strokeWidth="1" />
      <path d={area} fill={color} opacity="0.14" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" />
      <circle cx={pts[0][0]} cy={pts[0][1]} r="2" fill="#34d399" />
      <circle cx={pts[N][0]} cy={pts[N][1]} r="2" fill="#f87171" />
      <circle cx={apexPt[0]} cy={apexPt[1]} r="1.5" fill={color} />
      <text
        x={Math.min(Math.max(apexPt[0], 14), W - 14)}
        y={Math.max(apexPt[1] - 3, 8)}
        textAnchor="middle"
        fill={color}
        fontSize="8"
        fontFamily="monospace"
      >
        ↑{apex.toFixed(1)}m
      </text>
      <text x={W - PX - 1} y={groundY + 6.5} textAnchor="end" fill="#a1a1aa" fontSize="7" fontFamily="monospace">
        {Number(lengthM ?? 0).toFixed(0)}m · {T.toFixed(1)}s
      </text>
    </svg>
  );
}
